const pages = {
  en: {
    about: {
      meta: {
        title: 'About Leveltry | Professional advisory platform',
        description:
          'Learn what Leveltry is, how it works, and why people and advisors trust the platform.',
      },
      eyebrow: 'About us',
      title: 'Advisory that connects experience with real decisions',
      lead:
        'Leveltry is a platform where people find strategic, financial, and operational guidance from verified advisors, through private and structured consultations.',
      sections: [
        {
          title: 'Our mission',
          paragraphs: [
            'We believe good advice should be accessible, clear, and confidential. Leveltry removes friction so you can ask what matters and receive actionable guidance without unnecessary noise.',
            'Whether you need a second opinion, help with a project, or support for a critical decision, the platform connects you with professionals who have been there before.',
          ],
        },
        {
          title: 'How it works',
          paragraphs: [
            'Create your account, explore advisor profiles, and send a consultation with your context. Advisors respond on their own schedule, and you track everything in your activity feed.',
            'Each conversation is private. You choose who to consult, what to share, and when to continue.',
          ],
          list: [
            'Browse advisors by specialty and rating',
            'Send private consultations with clear context',
            'Receive structured answers you can act on',
            'Track history and progress in one place',
          ],
        },
        {
          title: 'Why Leveltry',
          paragraphs: [
            'Unlike open forums or generic chat, Leveltry is built for professional advisory: verified profiles, confidentiality, and a format designed for real decisions.',
          ],
          list: [
            'Confidentiality by design',
            'Verified advisor profiles with ratings',
            'Flexible format: quick questions or deeper projects',
            'Available wherever you are',
          ],
        },
      ],
      ctaTitle: 'Ready to get started?',
      ctaCopy: 'Create your account and send your first consultation today.',
      ctaButton: 'Register',
    },
    privacy: {
      meta: {
        title: 'Privacy Policy | Leveltry',
        description: 'How Leveltry collects, uses, and protects your personal data.',
      },
      eyebrow: 'Privacy',
      title: 'Privacy policy',
      lead: 'Last updated: May 2026. Your trust matters. This policy explains how we handle your information.',
      sections: [
        {
          title: 'Information we collect',
          paragraphs: [
            'We collect information you provide when registering, completing your profile, sending consultations, or contacting support. This may include your name, email, profile data, messages, and payment-related metadata when applicable.',
            'We also collect technical data such as device type, browser, and usage logs to keep the platform secure and improve the experience.',
          ],
        },
        {
          title: 'How we use your information',
          paragraphs: ['We use your data to:'],
          list: [
            'Operate the platform and deliver consultations',
            'Connect users with advisors securely',
            'Process payments and prevent fraud',
            'Improve features and support',
            'Comply with legal obligations',
          ],
        },
        {
          title: 'Confidentiality of consultations',
          paragraphs: [
            'Consultations are private between you and the advisor you choose. Leveltry does not sell consultation content to third parties. Access is limited to what is necessary to operate the service, resolve disputes, or comply with the law.',
          ],
        },
        {
          title: 'Sharing of information',
          paragraphs: [
            'We do not sell your personal data. We may share information with infrastructure providers, payment processors, or authorities when required by law or to protect users and the platform.',
          ],
        },
        {
          title: 'Your rights',
          paragraphs: ['Depending on your location, you may have the right to:'],
          list: [
            'Access, correct, or delete your data',
            'Object to or restrict certain processing',
            'Withdraw consent where applicable',
            'Request a copy of your information',
          ],
        },
        {
          title: 'Security and retention',
          paragraphs: [
            'We apply technical and organizational measures to protect your data. We retain information only as long as needed to provide the service, meet legal requirements, or resolve disputes.',
          ],
        },
        {
          title: 'Contact',
          paragraphs: [
            'For privacy questions or requests, contact us at privacy@leveltry.com or through the contact form on our website.',
          ],
        },
      ],
    },
    terms: {
      meta: {
        title: 'Terms and Conditions | Leveltry',
        description: 'Terms of use for the Leveltry advisory platform.',
      },
      eyebrow: 'Legal',
      title: 'Terms and conditions',
      lead: 'Last updated: May 2026. By using Leveltry, you agree to these terms.',
      sections: [
        {
          title: 'Acceptance of terms',
          paragraphs: [
            'By creating an account or using Leveltry, you accept these terms and our privacy policy. If you do not agree, do not use the platform.',
          ],
        },
        {
          title: 'Nature of the service',
          paragraphs: [
            'Leveltry is a technology platform that connects users with independent advisors. Advisors are not employees of Leveltry. Responses represent the advisor\'s professional opinion and do not constitute legal, tax, medical, or investment advice unless the advisor is explicitly licensed to provide such advice in your jurisdiction.',
          ],
        },
        {
          title: 'User responsibilities',
          paragraphs: ['As a user, you agree to:'],
          list: [
            'Provide accurate information in your account',
            'Use the platform lawfully and respectfully',
            'Not share content that is illegal, harmful, or infringing',
            'Evaluate advice and make your own decisions',
          ],
        },
        {
          title: 'Advisor responsibilities',
          paragraphs: ['As an advisor, you agree to:'],
          list: [
            'Provide truthful information in your profile',
            'Respond professionally and within agreed timelines when possible',
            'Respect confidentiality of consultations',
            'Comply with applicable laws and professional standards in your field',
          ],
        },
        {
          title: 'Payments and fees',
          paragraphs: [
            'Certain consultations, gifts, or premium features may involve fees. Prices and payment terms are shown before you confirm a transaction. Leveltry may charge platform fees as disclosed at checkout or in advisor agreements.',
          ],
        },
        {
          title: 'Limitation of liability',
          paragraphs: [
            'Leveltry is provided "as is." To the extent permitted by law, Leveltry is not liable for indirect damages, lost profits, or decisions made based on advisory content. Our total liability is limited to the amount you paid to Leveltry in the twelve months before the claim.',
          ],
        },
        {
          title: 'Changes and termination',
          paragraphs: [
            'We may update these terms or suspend accounts that violate them. Continued use after changes constitutes acceptance of the updated terms.',
          ],
        },
      ],
    },
    advisors: {
      meta: {
        title: 'Become an Advisor | Leveltry',
        description:
          'Earn extra income sharing your experience. Flexible schedule, verified profile, and private consultations on Leveltry.',
      },
      eyebrow: 'For advisors',
      title: 'Turn your experience into income on your own schedule',
      lead:
        'Join Leveltry as an advisor, help people make better decisions, and get paid for consultations, projects, and tips — without giving up your current job.',
      sections: [
        {
          title: 'Why register as an advisor',
          paragraphs: [
            'You already solve these problems every day. Leveltry gives you a channel to monetize that knowledge with people who are actively looking for guidance.',
          ],
          list: [
            'Extra income from your expertise',
            'Work when you want, from anywhere',
            'Build reputation with ratings and portfolio',
            'No need to find clients on your own',
          ],
        },
        {
          title: 'How you earn',
          paragraphs: [
            'Income comes from paid consultations users send to your profile, project-based advisory when you accept it, and optional gifts (tips) users can attach to their message.',
            'You set your availability. You decide which consultations to accept. Payments are processed through the platform so you can focus on delivering value.',
          ],
          list: [
            'Private 1:1 consultations',
            'Participation in open consultation pools',
            'Tips and gifts from users',
            'Longer projects when both sides agree',
          ],
        },
        {
          title: 'Who it is for',
          paragraphs: [
            'Professionals with real experience in strategy, finance, operations, accounting, technology, HR, legal (where permitted), and other fields where people need practical guidance.',
          ],
        },
        {
          title: 'How to get started',
          paragraphs: ['Registration is simple:'],
          list: [
            'Create your advisor account',
            'Complete your profile, specialties, and portfolio',
            'Set your availability and consultation preferences',
            'Start receiving requests and responding on your schedule',
          ],
        },
        {
          title: 'Trust and protection',
          paragraphs: [
            'Consultations are confidential. The platform provides structure, ratings, and support so you can focus on advising. Leveltry handles discovery; you deliver expertise.',
          ],
        },
      ],
      stats: [
        { value: '100%', label: 'Confidential consultations' },
        { value: '1:1', label: 'Direct relationship with users' },
        { value: '24/7', label: 'Respond on your schedule' },
      ],
      ctaTitle: 'Start earning with your experience',
      ctaCopy: 'Register as an advisor and publish your profile in minutes.',
      ctaButton: 'Register as advisor',
    },
  },
  es: {
    about: {
      meta: {
        title: 'Acerca de Leveltry',
        description:
          'Conoce qué es Leveltry, cómo funciona y por qué usuarios y asesores confían en la plataforma.',
      },
      eyebrow: 'Quiénes somos',
      title: 'Asesoría que conecta experiencia con decisiones reales',
      lead:
        'Leveltry es una plataforma donde personas encuentran orientación estratégica, financiera y operativa de asesores verificados, a través de consultas privadas y estructuradas.',
      sections: [
        {
          title: 'Nuestra misión',
          paragraphs: [
            'Creemos que una buena asesoría debe ser accesible, clara y confidencial. Leveltry elimina fricción para que preguntes lo que importa y recibas orientación accionable sin ruido innecesario.',
            'Ya sea que necesites una segunda opinión, ayuda con un proyecto o apoyo en una decisión crítica, la plataforma te conecta con profesionales que ya han pasado por eso.',
          ],
        },
        {
          title: 'Cómo funciona',
          paragraphs: [
            'Crea tu cuenta, explora perfiles de asesores, calificación, portafolio, experiencia y envía una consulta con tu contexto, para que los asesores se motiven a responder se les envia un regalo tipo propina que tu elijas según la complejidad de la consulta. Los asesores responden en su propio horario y tú sigues todo en tu actividad.',
            'Cada conversación es privada. Tú eliges a quién consultar, qué compartir y cuándo continuar.',
          ],
          list: [
            'Explora asesores por especialidad y calificación',
            'Envía consultas privadas con contexto claro',
            'Recibe respuestas estructuradas y accionables',
            'Lleva historial y progreso en un solo lugar',
          ],
        },
        {
          title: 'Por qué Leveltry',
          paragraphs: [
            'A diferencia de foros abiertos o chats genéricos, Leveltry está pensado para asesoría profesional: perfiles verificados, confidencialidad y un formato diseñado para decisiones reales.',
          ],
          list: [
            'Confidencialidad por diseño',
            'Perfiles verificados con valoraciones',
            'Formato flexible: preguntas rápidas o proyectos',
            'Disponible donde estés',
          ],
        },
      ],
      ctaTitle: '¿Listo para empezar?',
      ctaCopy: 'Crea tu cuenta y envía tu primera consulta hoy.',
      ctaButton: 'Registrarse',
    },
    privacy: {
      meta: {
        title: 'Leveltry',
        description: 'Cómo Leveltry recopila, usa y protege tus datos personales.',
      },
      eyebrow: 'Privacidad',
      title: 'Política de privacidad',
      lead: 'Última actualización: mayo 2026. Tu confianza importa. Esta política explica cómo tratamos tu información.',
      sections: [
        {
          title: 'Información que recopilamos',
          paragraphs: [
            'Recopilamos la información que proporcionas al registrarte, completar tu perfil, enviar consultas o contactar soporte. Esto puede incluir nombre, correo, datos de perfil, mensajes y metadatos de pago cuando aplique.',
            'También recopilamos datos técnicos como tipo de dispositivo, navegador y registros de uso para mantener la plataforma segura y mejorar la experiencia.',
          ],
        },
        {
          title: 'Cómo usamos tu información',
          paragraphs: ['Usamos tus datos para:'],
          list: [
            'Operar la plataforma y entregar consultas',
            'Conectar usuarios con asesores de forma segura',
            'Procesar pagos y prevenir fraude',
            'Mejorar funciones y soporte',
            'Cumplir obligaciones legales',
          ],
        },
        {
          title: 'Confidencialidad de las consultas',
          paragraphs: [
            'Las consultas son privadas entre tú y el asesor que elijas. Leveltry no vende el contenido de las consultas a terceros. El acceso se limita a lo necesario para operar el servicio, resolver disputas o cumplir la ley.',
          ],
        },
        {
          title: 'Compartición de información',
          paragraphs: [
            'No vendemos tus datos personales. Podemos compartir información con proveedores de infraestructura, procesadores de pago o autoridades cuando la ley lo exija o para proteger a usuarios y la plataforma.',
          ],
        },
        {
          title: 'Tus derechos',
          paragraphs: ['Según tu ubicación, puedes tener derecho a:'],
          list: [
            'Acceder, corregir o eliminar tus datos',
            'Oponerte o restringir cierto tratamiento',
            'Retirar el consentimiento cuando aplique',
            'Solicitar una copia de tu información',
          ],
        },
        {
          title: 'Seguridad y conservación',
          paragraphs: [
            'Aplicamos medidas técnicas y organizativas para proteger tus datos. Conservamos la información solo el tiempo necesario para prestar el servicio, cumplir requisitos legales o resolver disputas.',
          ],
        },
        {
          title: 'Disputas y resolución',
          paragraphs: [
            'Leveltry no es responsable de las decisiones tomadas por los usuarios o asesores. Si tienes una disputa, puedes contactar a soporte@leveltry.com.',
            'El usuario o asesor puede presentar una reclamación ante la autoridad de protección de datos de su jurisdicción.',
            'Leveltry no es responsable de las respuestas de los usuarios o asesores.',
            'Los regalos (propinas) no son reembolsables y no garantizan respuestas específicas.',
            'Leveltry no es responsable de convenios y malos tratos que se realicen entre usuarios y asesores fuera de la plataforma.',

          ],
        },
        {
          title: 'Contacto',
          paragraphs: [
            'Para consultas o solicitudes de privacidad, escríbenos a team@leveltry.com o mediante el formulario de contacto en el sitio.',
          ],
        },
      ],
    },
    terms: {
      meta: {
        title: 'Leveltry',
        description: 'Términos de uso de la plataforma de asesoría Leveltry.',
      },
      eyebrow: 'Legal',
      title: 'Términos y condiciones',
      lead: 'Última actualización: mayo 2026. Al usar Leveltry, aceptas estos términos.',
      sections: [
        {
          title: 'Aceptación de los términos',
          paragraphs: [
            'Al crear una cuenta o usar Leveltry, aceptas estos términos y nuestra política de privacidad. Si no estás de acuerdo, no uses la plataforma.',
          ],
        },
        {
          title: 'Naturaleza del servicio',
          paragraphs: [
            'Leveltry es una plataforma tecnológica que conecta usuarios con asesores independientes. Los asesores no son empleados de Leveltry. Las respuestas representan la opinión profesional del asesor y no constituyen asesoría legal, fiscal, médica o de inversión salvo que el asesor esté expresamente habilitado en tu jurisdicción.',
          ],
        },
        {
          title: 'Responsabilidades del usuario',
          paragraphs: ['Como usuario, te comprometes a:'],
          list: [
            'Proporcionar información veraz en tu cuenta',
            'Usar la plataforma de forma legal y respetuosa',
            'No compartir contenido ilegal, dañino o que infrinja derechos',
            'Evaluar la asesoría y tomar tus propias decisiones',
            'No exigir respuestas específicas o garantías de éxito',
            'No usar la plataforma para fines ilícitos o fraudulentos',
            'No usar la plataforma para fines de spam o spamming',
            'No usar la plataforma para fines de phishing',
            'No usar la plataforma para fines de dumping',
            'No usar lenguaje ofensivo o discriminatorio',
            'Entender que las respuestas son opiniones y no asesoría legal, fiscal, médica o de inversión',
            'Enteder que los regalos son propinas y no son reembolsables',
            'Entender que la plataforma es solo un medio para la asesoría y no es responsable de las decisiones tomadas por el usuario',
            'Entender que las propinas del cualquier costo no garantizan respuestas específicas',
          ],
        },
        {
          title: 'Responsabilidades del asesor',
          paragraphs: ['Como asesor, te comprometes a:'],
          list: [
            'Ofrecer información veraz en tu perfil',
            'Responder de forma profesional y en los plazos acordados cuando sea posible',
            'Respetar la confidencialidad de las consultas',
            'Cumplir las leyes y estándares profesionales de tu ámbito',
            'No usar la plataforma para fines ilícitos o fraudulentos',
            'No usar la plataforma para fines de spam o spamming',
            'No usar la plataforma para fines de phishing',
            'No usar la plataforma para fines de dumping',
            'No usar lenguaje ofensivo o discriminatorio',
            'Entender que las consultas son privadas y no se pueden compartir con terceros',
            'Entender que si las consultas no son respondidas o son omitidas, las propinas seran reembolsadas al usuario',
            'Entender que el usuario calificara la asesoría en base a la consulta, y que la calificación sera visible para todos',
            'Entender que la plataforma es solo un medio para la asesoría y no es responsable de las decisiones tomadas por el usuario',
            'Entender que si las consultas no estan en entendimiento del asesor es mejor no responder y no recibir la propina',
            'Entender que si la calidad de la asesoría no es la esperada, el usuario puede calificar la asesoría en base a la consulta, y que la calificación sera visible para todos',
            'Mantener la comunicación dentro de la plataforma para resolver cualquier duda o problema que se presente.',
            'Entender que el tiempo en la plataforma en responder consultas es remunerado con regalos (propinas) y que Leveltry no es responsable por el tiempo, convenios y malos tratos que se realicen entre usuarios y asesores fuera de la plataforma.',

          ],
        },
        {
          title: 'Pagos, Retiros y comisiones',
          paragraphs: [
            'Las consultas se envian con regalos propina y cada regalo tiene un costo en monedas virtuales que se compra en la plataforma por paquetes con dinero real. Los precios y condiciones de pago se muestran antes de confirmar. Leveltry puede aplicar comisiones de plataforma según se indique al pagar.',
            'El asesor recibe el pago de la propina con monedas virtuales en su cuenta de Leveltry, que puede ser convertidas a dinero real en su cuenta bancaria o en su cuenta de PayPal, según lo acordado.',
            'Leveltry puede aplicar comisiones de plataforma según se indique al realizar retiros de dinero real.',
            'El asesor debe llegar a un monto minimo de 100000 monedas virtuales para poder realizar un retiro de dinero real.',
          ],
        },
        {
          title: 'Limitación de responsabilidad',
          paragraphs: [
            'Leveltry se ofrece "tal cual". En la medida permitida por la ley, Leveltry no es responsable por daños indirectos, lucro cesante o decisiones tomadas con base en el contenido de la asesoría. Nuestra responsabilidad total no se limita al monto que hayas pagado a Leveltry.',
          ],
        },
        {
          title: 'Cambios y terminación',
          paragraphs: [
            'Podemos actualizar estos términos o suspender cuentas que los incumplan. El uso continuado tras los cambios implica aceptación de los términos actualizados.',
          ],
        },
      ],
    },
    advisors: {
      meta: {
        title: 'Leveltry',
        description:
          'Obtén ingresos extra compartiendo tu experiencia. Horario flexible, perfil verificado y consultas privadas en Leveltry.',
      },
      eyebrow: 'Para asesores',
      title: 'Convierte tu experiencia en ingresos a tu ritmo',
      lead:
        'Únete a Leveltry como asesor, ayuda a personas a tomar mejores decisiones y recibe pagos propinas por consultas, proyectos y más — sin dejar tu trabajo actual.',
      sections: [
        {
          title: 'Por qué registrarte como asesor',
          paragraphs: [
            'Ya resuelves estos problemas cada día. Leveltry te da un canal para monetizar ese conocimiento con personas que buscan orientación activamente.',
          ],
          list: [
            'Ingresos extra con tu experiencia',
            'Trabaja cuando quieras, desde donde quieras',
            'Construye reputación con valoraciones y portafolio',
            'Sin buscar clientes por tu cuenta',
          ],
        },
        {
          title: 'Cómo ganas',
          paragraphs: [
            'Los ingresos provienen de consultas por regalos (propinas) que los usuarios envían a tu perfil, asesorías que bien detalladas confirman tu propina y ayudan a subir tu calificación.',
            'Tú defines tu disponibilidad. Tú decides qué consultas aceptar. Los pagos se procesan en la plataforma para que te enfoques en aportar valor.',
            'Entre mayor sea tu calificación, mayor sera la demanda de tus consultas.',
            'Entre mayor sea tu calificación, podras elegir regalos (propinas) minimos de mayor valor.',
          ],
          list: [
            'Consultas privadas 1:1',
            'Participación en pools de consultas abiertas',
            'Propinas y regalos de usuarios',
            'Proyectos más largos cuando ambas partes acuerden',
          ],
        },
        {
          title: 'Para quién es',
          paragraphs: [
            'Profesionales con experiencia real en estrategia, finanzas, operaciones, contabilidad, tecnología, RR. HH., legal (donde aplique) y otros campos donde la gente necesita orientación práctica.',
            'Personal que labora en posiciones estratégicas que puedan orientar a usuarios en decisiones reales, trámites, proyectos, etc.',
          ],
        },
        {
          title: 'Cómo empezar',
          paragraphs: ['Registrarse es sencillo:'],
          list: [
            'Crea tu cuenta de asesor',
            'Completa perfil, especialidades y portafolio',
            'Define disponibilidad y preferencias de consulta',
            'Recibe solicitudes y responde en tu horario',
          ],
        },
        {
          title: 'Confianza y protección',
          paragraphs: [
            'Las consultas son confidenciales. La plataforma ofrece estructura, valoraciones y soporte para que te enfoques en asesorar. Leveltry atrae demanda; tú aportas experiencia.',
            'El usuario puede calificar la asesoría en base a la consulta, y que la calificación sera visible para todos',
            'Mientras uses la plataforma te protegemos de usuarios mal intencionados, y del uso indebido de tu tiempo.'
          ],
        },
      ],
      stats: [
        { value: '100%', label: 'Consultas confidenciales' },
        { value: '1:1', label: 'Relación directa con usuarios' },
        { value: '24/7', label: 'Respondes en tu horario' },
      ],
      ctaTitle: 'Empieza a ganar con tu experiencia',
      ctaCopy: 'Regístrate como asesor y publica tu perfil en minutos.',
      ctaButton: 'Registrarse como asesor',
    },
  },
  pt: {
    about: {
      meta: {
        title: 'Leveltry',
        description:
          'Saiba o que é a Leveltry, como funciona e por que usuários e assessores confiam na plataforma.',
      },
      eyebrow: 'Quem somos',
      title: 'Assessoria que conecta experiência a decisões reais',
      lead:
        'A Leveltry é uma plataforma onde pessoas encontram orientação estratégica, financeira e operacional de assessores verificados, por meio de consultas privadas e estruturadas.',
      sections: [
        {
          title: 'Nossa missão',
          paragraphs: [
            'Acreditamos que uma boa assessoria deve ser acessível, clara e confidencial. A Leveltry remove atritos para que você pergunte o que importa e receba orientação acionável sem ruído desnecessário.',
            'Seja para uma segunda opinião, ajuda em um projeto ou apoio em uma decisão crítica, a plataforma conecta você a profissionais que já passaram por isso.',
          ],
        },
        {
          title: 'Como funciona',
          paragraphs: [
            'Crie sua conta, explore perfis de assessores e envie uma consulta com seu contexto. Os assessores respondem no próprio ritmo e você acompanha tudo na sua atividade.',
            'Cada conversa é privada. Você escolhe com quem consultar, o que compartilhar e quando continuar.',
          ],
          list: [
            'Explore assessores por especialidade e avaliação',
            'Envie consultas privadas com contexto claro',
            'Receba respostas estruturadas e acionáveis',
            'Acompanhe histórico e progresso em um só lugar',
          ],
        },
        {
          title: 'Por que a Leveltry',
          paragraphs: [
            'Diferente de fóruns abertos ou chats genéricos, a Leveltry é feita para assessoria profissional: perfis verificados, confidencialidade e formato pensado para decisões reais.',
          ],
          list: [
            'Confidencialidade por design',
            'Perfis verificados com avaliações',
            'Formato flexível: perguntas rápidas ou projetos',
            'Disponível onde você estiver',
          ],
        },
      ],
      ctaTitle: 'Pronto para começar?',
      ctaCopy: 'Crie sua conta e envie sua primeira consulta hoje.',
      ctaButton: 'Cadastrar-se',
    },
    privacy: {
      meta: {
        title: 'Leveltry',
        description: 'Como a Leveltry coleta, usa e protege seus dados pessoais.',
      },
      eyebrow: 'Privacidade',
      title: 'Política de privacidade',
      lead: 'Última atualização: maio de 2026. Sua confiança importa. Esta política explica como tratamos suas informações.',
      sections: [
        {
          title: 'Informações que coletamos',
          paragraphs: [
            'Coletamos informações que você fornece ao se cadastrar, completar o perfil, enviar consultas ou contatar o suporte. Isso pode incluir nome, e-mail, dados de perfil, mensagens e metadados de pagamento quando aplicável.',
            'Também coletamos dados técnicos como tipo de dispositivo, navegador e registros de uso para manter a plataforma segura e melhorar a experiência.',
          ],
        },
        {
          title: 'Como usamos suas informações',
          paragraphs: ['Usamos seus dados para:'],
          list: [
            'Operar a plataforma e entregar consultas',
            'Conectar usuários a assessores com segurança',
            'Processar pagamentos e prevenir fraudes',
            'Melhorar recursos e suporte',
            'Cumprir obrigações legais',
          ],
        },
        {
          title: 'Confidencialidade das consultas',
          paragraphs: [
            'As consultas são privadas entre você e o assessor escolhido. A Leveltry não vende o conteúdo das consultas a terceiros. O acesso é limitado ao necessário para operar o serviço, resolver disputas ou cumprir a lei.',
          ],
        },
        {
          title: 'Compartilhamento de informações',
          paragraphs: [
            'Não vendemos seus dados pessoais. Podemos compartilhar informações com provedores de infraestrutura, processadores de pagamento ou autoridades quando exigido por lei ou para proteger usuários e a plataforma.',
          ],
        },
        {
          title: 'Seus direitos',
          paragraphs: ['Dependendo da sua localização, você pode ter direito a:'],
          list: [
            'Acessar, corrigir ou excluir seus dados',
            'Opor-se ou restringir certos tratamentos',
            'Retirar consentimento quando aplicável',
            'Solicitar uma cópia das suas informações',
          ],
        },
        {
          title: 'Segurança e retenção',
          paragraphs: [
            'Aplicamos medidas técnicas e organizacionais para proteger seus dados. Retemos informações apenas pelo tempo necessário para prestar o serviço, cumprir requisitos legais ou resolver disputas.',
          ],
        },
        {
          title: 'Contato',
          paragraphs: [
            'Para dúvidas ou solicitações de privacidade, escreva para privacy@leveltry.com ou use o formulário de contato no site.',
          ],
        },
      ],
    },
    terms: {
      meta: {
        title: 'Leveltry',
        description: 'Termos de uso da plataforma de assessoria Leveltry.',
      },
      eyebrow: 'Legal',
      title: 'Termos e condições',
      lead: 'Última atualização: maio de 2026. Ao usar a Leveltry, você aceita estes termos.',
      sections: [
        {
          title: 'Aceitação dos termos',
          paragraphs: [
            'Ao criar uma conta ou usar a Leveltry, você aceita estes termos e nossa política de privacidade. Se não concordar, não use a plataforma.',
          ],
        },
        {
          title: 'Natureza do serviço',
          paragraphs: [
            'A Leveltry é uma plataforma tecnológica que conecta usuários a assessores independentes. Os assessores não são funcionários da Leveltry. As respostas representam a opinião profissional do assessor e não constituem assessoria jurídica, fiscal, médica ou de investimento, salvo quando o assessor estiver expressamente habilitado na sua jurisdição.',
          ],
        },
        {
          title: 'Responsabilidades do usuário',
          paragraphs: ['Como usuário, você concorda em:'],
          list: [
            'Fornecer informações verdadeiras na conta',
            'Usar a plataforma de forma legal e respeitosa',
            'Não compartilhar conteúdo ilegal, prejudicial ou que viole direitos',
            'Avaliar a assessoria e tomar suas próprias decisões',
          ],
        },
        {
          title: 'Responsabilidades do assessor',
          paragraphs: ['Como assessor, você concorda em:'],
          list: [
            'Fornecer informações verdadeiras no perfil',
            'Responder de forma profissional e nos prazos acordados quando possível',
            'Respeitar a confidencialidade das consultas',
            'Cumprir leis e padrões profissionais da sua área',
          ],
        },
        {
          title: 'Pagamentos e taxas',
          paragraphs: [
            'Algumas consultas, presentes ou recursos premium podem ter custo. Preços e condições de pagamento são exibidos antes da confirmação. A Leveltry pode cobrar taxas de plataforma conforme indicado no checkout ou em acordos com assessores.',
          ],
        },
        {
          title: 'Limitação de responsabilidade',
          paragraphs: [
            'A Leveltry é fornecida "como está". Na medida permitida por lei, a Leveltry não se responsabiliza por danos indiretos, lucros cessantes ou decisões tomadas com base no conteúdo da assessoria. Nossa responsabilidade total limita-se ao valor pago à Leveltry nos doze meses anteriores à reclamação.',
          ],
        },
        {
          title: 'Alterações e rescisão',
          paragraphs: [
            'Podemos atualizar estes termos ou suspender contas que os violem. O uso continuado após alterações implica aceitação dos termos atualizados.',
          ],
        },
      ],
    },
    advisors: {
      meta: {
        title: 'Leveltry',
        description:
          'Ganhe renda extra compartilhando sua experiência. Horário flexível, perfil verificado e consultas privadas na Leveltry.',
      },
      eyebrow: 'Para assessores',
      title: 'Transforme sua experiência em renda no seu ritmo',
      lead:
        'Junte-se à Leveltry como assessor, ajude pessoas a tomar melhores decisões e receba por consultas, projetos e gorjetas — sem abandonar seu trabalho atual.',
      sections: [
        {
          title: 'Por que se cadastrar como assessor',
          paragraphs: [
            'Você já resolve esses problemas todos os dias. A Leveltry oferece um canal para monetizar esse conhecimento com pessoas que buscam orientação ativamente.',
          ],
          list: [
            'Renda extra com sua experiência',
            'Trabalhe quando quiser, de onde quiser',
            'Construa reputação com avaliações e portfólio',
            'Sem precisar buscar clientes sozinho',
          ],
        },
        {
          title: 'Como você ganha',
          paragraphs: [
            'A renda vem de consultas pagas enviadas ao seu perfil, assessorias por projeto quando você aceita, e presentes (gorjetas) opcionais que o usuário pode anexar à mensagem.',
            'Você define sua disponibilidade. Você decide quais consultas aceitar. Os pagamentos são processados na plataforma para você focar em entregar valor.',
          ],
          list: [
            'Consultas privadas 1:1',
            'Participação em pools de consultas abertas',
            'Gorjetas e presentes dos usuários',
            'Projetos mais longos quando ambas as partes concordarem',
          ],
        },
        {
          title: 'Para quem é',
          paragraphs: [
            'Profissionais com experiência real em estratégia, finanças, operações, contabilidade, tecnologia, RH, jurídico (onde aplicável) e outras áreas em que as pessoas precisam de orientação prática.',
          ],
        },
        {
          title: 'Como começar',
          paragraphs: ['O cadastro é simples:'],
          list: [
            'Crie sua conta de assessor',
            'Complete perfil, especialidades e portfólio',
            'Defina disponibilidade e preferências de consulta',
            'Receba solicitações e responda no seu horário',
          ],
        },
        {
          title: 'Confiança e proteção',
          paragraphs: [
            'As consultas são confidenciais. A plataforma oferece estrutura, avaliações e suporte para você focar em assessorar. A Leveltry gera demanda; você entrega experiência.',
          ],
        },
      ],
      stats: [
        { value: '100%', label: 'Consultas confidenciais' },
        { value: '1:1', label: 'Relação direta com usuários' },
        { value: '24/7', label: 'Responda no seu horário' },
      ],
      ctaTitle: 'Comece a ganhar com sua experiência',
      ctaCopy: 'Cadastre-se como assessor e publique seu perfil em minutos.',
      ctaButton: 'Cadastrar como assessor',
    },
  },
};

export function getPageContent(locale, pageKey) {
  const localePages = pages[locale] || pages.en;
  return localePages[pageKey] || pages.en[pageKey];
}
